import type { ReactNode } from "react";

import { Toolbar } from "./compact/Toolbar";

/**
 * The one eyebrow convention.
 *
 * The app had three, one per screen — `TUESDAY, AUGUST 4, 2026` on tasks,
 * `TODAY · TUE, AUG 4` on workout, `PROJECTS` on kanban — so the same line in
 * the same position meant a different kind of thing on each surface and none
 * of them could be scanned against the others.
 *
 * Settled shape, and it is the middle one:
 *
 *     WHERE YOU ARE · WHAT IT IS
 *
 * ```ts
 * pageEyebrow("Today", "Tue, Aug 4")  // TODAY · TUE, AUG 4
 * pageEyebrow("Projects")             // PROJECTS
 * ```
 */
export function pageEyebrow(...parts: readonly (string | null | undefined)[]): string {
  return parts
    .filter((part): part is string => Boolean(part && part.trim()))
    .join(" · ")
    .toUpperCase();
}

export type PageHeaderProps = {
  /** Build it with {@link pageEyebrow}; do not hand-write the separator. */
  eyebrow?: ReactNode;
  /**
   * Presentation for the title. The string `title` remains the page's
   * canonical accessible name. Use this for a typewriter reveal.
   */
  titleContent?: ReactNode;
  title: string;
  /** Folded into the toolbar's single truncating meta line. */
  description?: ReactNode;
  /** Trailing controls in the toolbar. */
  actions?: ReactNode;
  /** A leading toolbar action: a back chevron, a day stepper. */
  leading?: ReactNode;
  /** Anything that belongs in the 40/44px row under the sticky toolbar. */
  accessory?: ReactNode;
  /**
   * Kept while call sites migrate from the collapsing header. The compact
   * toolbar has no second state, so this value no longer changes rendering.
   */
  collapsing?: boolean;
  /**
   * Makes the title itself the trigger for a menu. The caller owns the menu
   * and receives the title plus its selector chevron as one flex-ready node.
   */
  titleMenu?: (title: ReactNode) => ReactNode;
  className?: string;
};

/**
 * Compatibility spelling for the app's compact {@link Toolbar}.
 *
 * The old component rendered a 44px navigation bar, a separate 34px large
 * title, and a second hidden title for the collapsed state. The toolbar is one
 * 64px bar and one `<h1>`. Existing eyebrow and description copy share its
 * single meta line; `titleContent`, `titleMenu`, leading/trailing actions and
 * the accessory strip pass through without losing their DOM or interaction.
 */
export function PageHeader({
  eyebrow,
  titleContent,
  title,
  description,
  actions,
  leading,
  accessory,
  collapsing: _collapsing,
  titleMenu,
  className,
}: PageHeaderProps) {
  const meta = eyebrow || description ? (
    <>
      {eyebrow}
      {eyebrow && description ? <span className="text-faint"> · </span> : null}
      {description}
    </>
  ) : undefined;

  return (
    <Toolbar
      title={title}
      titleContent={titleContent}
      titleMenu={titleMenu}
      meta={meta}
      leading={leading}
      actions={actions}
      accessory={accessory}
      className={className}
    />
  );
}
